import Service from '@ember/service';

import { action } from '@ember/object';
import { once } from '@ember/runloop';
import { service } from '@ember/service';

import { getSize } from 'rise-ui/utils/dom.js';
import { ResizeObserver } from 'rise-ui/utils/resize-observer.js';
import { byBoolean, byDocumentPosition, byPipe, byPath } from 'rise-ui/utils/sort.js';

const matchDelimiters = /[\s,]+/;

// TODO might want to hide certain stickies on touch idle (see landscape)
export default class StickyService extends Service {
  @service viewport;

  resizing = new ResizeObserver(this.update);

  stickies = new Map();
  activators = new Map();

  isScrollingUp = false;

  constructor() {
    super(...arguments);
    this.viewport.observeScrollDirection(this.onScrollingDirectionChange);
  }

  @action onScrollingDirectionChange(isUp) {
    this.isScrollingUp = isUp;
    this.update();
  }

  add(el, name, pause) {
    const sticky = this.stickies.get(el);
    if (sticky) {
      return;
    }
    this.stickies.set(el, { el, name, pause });
    this.resizing.observe(el);
    this.update(el);
  }

  remove(el) {
    const sticky = this.stickies.get(el);
    if (!sticky) {
      return;
    }
    this.resizing.unobserve(el);
    this.stickies.delete(el);
    el.style.position = undefined;
    el.style.visibility = undefined;
    el.style.zIndex = undefined;
    el.style.top = undefined;
    el.style.right = undefined;
    el.style.bottom = undefined;
    el.style.left = undefined;
    sticky.onChange?.(false);
    this.update(el);
  }

  activate(stickyMod) {
    if (this.activators.has(stickyMod)) {
      return;
    }

    let isIntersecting;
    let isAbove;
    let isSticky;

    const { element: el, activate: names, class: cls, onChange } = stickyMod;

    const activates = [].concat(...([].concat(names || []).map(name =>
      typeof name === 'string' ? name.split(matchDelimiters) : []
    )));

    const sticky = {
      el,
      activates,
      onChange,
      deactivate: () => {
        this.viewport.removeObserver(el);
        this.viewport.unobserveScrollDirection(setSticky);
        this.resizing.unobserve(el);
        this.activators.delete(stickyMod);
        this.update(el);
      },
    };

    const setSticky = () => {
      const isNowSticky = !isIntersecting && isAbove;
      if (isSticky === isNowSticky || isNowSticky === this.isScrollingUp) {
        return;
      }
      sticky.isSticky = isSticky = isNowSticky;
      if (isSticky) {
        onChange?.(isSticky);
      }
      if (cls) {
        el.classList[ isSticky ? 'add' : 'remove' ](cls);
      }
      this.update(el);
    };

    function onIntersection(entry) {
      isIntersecting = entry.isIntersecting;
      isAbove = entry.boundingClientRect.top < entry.rootBounds.top;
      setSticky();
    }

    this.activators.set(stickyMod, sticky);
    this.viewport.addObserver(el, onIntersection);
    this.viewport.observeScrollDirection(setSticky);
    this.resizing.observe(el);

    this.update(el);
  }

  deactivate(stickyMod) {
    this.activators.get(stickyMod)?.deactivate();
  }

  _updateEls = new Set();

  @action update(els) {
    [].concat(els || []).forEach(el => this._updateEls.add(el));
    once(this, '_update');
  }

  _update() {
    const els = Array.from(this._updateEls);
    this._updateEls.clear();

    const names = new Set();
    const activated = new Set();
    const allStickies = [ ...this.stickies.values(), ...this.activators.values() ]
      .sort(byPipe(
        byPath('el', byDocumentPosition()),
        byPath('activates', byBoolean()),
      ));
    const visibleStickies = [];

    for (let i = allStickies.length - 1; i >= 0; --i) {
      const sticky = allStickies[i];
      const { el, name, pause, activates, isSticky, active = false } = sticky;

      if (activates) {
        if (isSticky) {
          activates.forEach(name => {
            activated.add(name);
          });
        }
        continue;
      }

      if (names.has(name) || !activated.has(name)
        // || ( ? (active || this.isScrollingUp) : (!active || !this.isScrollingUp))
      ) {
        el.style.visibility = 'hidden';
        sticky.active = false;
        continue;
      }

      names.add(name);
      visibleStickies.unshift(sticky);
      sticky.active = true;

      if (pause) {
        [].concat(pause).forEach(p => names.add(p));
      }
    }

    let top = 0;

    for (const { el } of visibleStickies) {
      el.style.position = 'fixed';
      el.style.visibility = 'visible';
      el.style.zIndex = 9998;
      el.style.top = top + 'px';
      el.style.right = 0;
      el.style.bottom = undefined;
      el.style.left = 0;
      const style = getComputedStyle(el);
      top += (style.display === 'none' ? 0 : getSize(el).height || 0) - 1;
    }

    //   || isSticky === this.viewport.isScrollingDirectionUp
    this.viewport.updateOverlay('sticky', 'top', top);
  }
}
